import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../../../../state/state.tsx';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2.ts';
import { db, storage } from '../../../../../Config/firebase.ts';
import { 
    collection, 
    addDoc, 
    query, 
    orderBy, 
    onSnapshot, 
    deleteDoc, 
    doc, 
    updateDoc,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import { 
    ref as storageRef, 
    uploadBytes, 
    getDownloadURL, 
    deleteObject 
} from 'firebase/storage';
import { toast } from 'react-toastify';

/**
 * ChatSection Component - Reusable Floating Chat Widget
 * 
 * This component provides a floating, draggable chat interface that can be used in:
 * 1. Subscription Management App (context: 'subscription')
 * 2. Client App (context: 'client')
 * 
 * Features:
 * - Real-time messaging with Firebase Firestore
 * - Image upload support with Firebase Storage
 * - Message editing and deletion (only for own messages)
 * - Draggable positioning (when isDetachable=true)
 * - Different Firestore collections based on context
 * - Permission-based editing (users can only edit their own messages)
 * 
 * Usage Examples:
 * 
 * // In Subscription Management App
 * <ChatSection
 *   subscriptionId="sub_123"
 *   subscriptionName="Math Class"
 *   isDetachable={true}
 *   context="subscription"
 *   initialPosition={{ x: 100, y: 100 }}
 *   onClose={() => setShowChat(false)}
 * />
 * 
 * // In Client App (without Redux dependencies)
 * <ChatSection
 *   subscriptionId="sub_123"
 *   subscriptionName="Math Class"
 *   isDetachable={true}
 *   context="client"
 *   currentUserId="user_456"
 *   currentUserName="John Doe"
 *   onClose={() => setShowChat(false)}
 * />
 * 
 * // Embedded (non-detachable) version
 * <ChatSection
 *   subscriptionId="sub_123"
 *   subscriptionName="Math Class"
 *   isDetachable={false}
 *   context="subscription"
 * />
 */

interface ChatMessage {
    id: string;
    text: string;
    imageUrl?: string;
    senderId: string;
    senderName: string;
    timestamp: Timestamp;
    isEdited?: boolean;
}

interface ChatSectionProps {
    subscriptionId: string;
    subscriptionName: string;
    // New props for reusability
    isDetachable?: boolean;
    initialPosition?: { x: number; y: number };
    onClose?: () => void;
    // Props for different contexts (subscription vs client app)
    context?: 'subscription' | 'client';
    currentUserId?: string;
    currentUserName?: string;
}

export default function ChatSection({ 
    subscriptionId, 
    subscriptionName,
    isDetachable = true,
    initialPosition,
    onClose,
    context = 'subscription',
    currentUserId: propCurrentUserId,
    currentUserName: propCurrentUserName
}: ChatSectionProps) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    
    // Drag state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(initialPosition || { x: window.innerWidth - 350, y: 100 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatRef = useRef<HTMLDivElement>(null);
    
    // Get current user info - use props if provided, otherwise from Redux
    const reduxCurrentUserId = useAppSelector(selectEffectiveUID);
    const reduxCurrentUserName = useAppSelector((state) => (state.userSliceV2.data as any)?.display_name || 'Unknown User');
    
    const currentUserId = propCurrentUserId || reduxCurrentUserId;
    const currentUserName = propCurrentUserName || reduxCurrentUserName;

    // Use different collection paths based on context
    const getChatsCollectionRef = () => {
        if (context === 'client') {
            return collection(db, 'clientChats', subscriptionId, 'messages');
        }
        return collection(db, 'subscriptions', subscriptionId, 'chats');
    };

    const chatsCollectionRef = getChatsCollectionRef();

    // Check if user can edit a message (only their own messages)
    const canEditMessage = (message: ChatMessage) => {
        return message.senderId === currentUserId;
    };

    // Check if user can delete a message (only their own messages)
    const canDeleteMessage = (message: ChatMessage) => {
        return message.senderId === currentUserId;
    };

    // Handle drag start
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!isDetachable) return;
        
        if (chatRef.current) {
            const rect = chatRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    };

    // Handle drag move
    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && isDetachable) {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Keep chat within viewport bounds
            const maxX = window.innerWidth - 320; // chat width
            const maxY = window.innerHeight - 400; // chat height
            
            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        }
    };

    // Handle drag end
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Add/remove global mouse event listeners
    useEffect(() => {
        if (isDragging && isDetachable) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset, isDetachable]);

    // Prevent text selection while dragging
    useEffect(() => {
        if (isDragging && isDetachable) {
            document.body.style.userSelect = 'none';
            return () => {
                document.body.style.userSelect = '';
            };
        }
    }, [isDragging, isDetachable]);

    // Scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Listen to chat messages
    useEffect(() => {
        const q = query(chatsCollectionRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatMessages: ChatMessage[] = [];
            snapshot.forEach((doc) => {
                chatMessages.push({ id: doc.id, ...doc.data() } as ChatMessage);
            });
            setMessages(chatMessages);
        });

        return () => unsubscribe();
    }, [subscriptionId]);

    // Handle image file selection
    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }
            
            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            setSelectedFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload image to Firebase Storage
    const uploadImage = async (file: File): Promise<string> => {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const imageRef = storageRef(storage, `subscriptions/${subscriptionName}/${fileName}`);
        
        const snapshot = await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return downloadURL;
    };

    // Send message
    const sendMessage = async () => {
        if (!newMessage.trim() && !selectedFile) return;
        
        setIsLoading(true);
        setUploadingImage(true);
        
        try {
            let imageUrl = '';
            
            // Upload image if selected
            if (selectedFile) {
                imageUrl = await uploadImage(selectedFile);
            }
            
            // Add message to Firestore
            await addDoc(chatsCollectionRef, {
                text: newMessage.trim(),
                imageUrl: imageUrl || null,
                senderId: currentUserId,
                senderName: currentUserName,
                timestamp: serverTimestamp(),
                isEdited: false
            });
            
            // Reset form
            setNewMessage('');
            setSelectedFile(null);
            setImagePreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setIsLoading(false);
            setUploadingImage(false);
        }
    };

    // Delete message
    const deleteMessage = async (messageId: string, imageUrl?: string) => {
        try {
            // Delete image from storage if exists
            if (imageUrl) {
                const imageRef = storageRef(storage, imageUrl);
                await deleteObject(imageRef);
            }
            
            // Delete message from Firestore
            await deleteDoc(doc(chatsCollectionRef, messageId));
            toast.success('Message deleted');
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Failed to delete message');
        }
    };

    // Start editing message
    const startEditing = (message: ChatMessage) => {
        setEditingMessageId(message.id);
        setEditText(message.text);
    };

    // Save edited message
    const saveEdit = async () => {
        if (!editText.trim()) return;
        
        try {
            await updateDoc(doc(chatsCollectionRef, editingMessageId!), {
                text: editText.trim(),
                isEdited: true
            });
            
            setEditingMessageId(null);
            setEditText('');
            toast.success('Message updated');
        } catch (error) {
            console.error('Error updating message:', error);
            toast.error('Failed to update message');
        }
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingMessageId(null);
        setEditText('');
    };

    // Remove image preview
    const removeImagePreview = () => {
        setSelectedFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle Enter key
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Format timestamp
    const formatTimestamp = (timestamp: Timestamp | null) => {
        if (!timestamp) return 'Just now';
        
        try {
            const date = timestamp.toDate();
            const now = new Date();
            const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
            
            if (diffInHours < 24) {
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else {
                return date.toLocaleDateString();
            }
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return 'Just now';
        }
    };

    return (
        <div 
            ref={chatRef}
            className={`${isDetachable ? 'fixed' : 'relative'} w-full h-full bg-white rounded-lg border border-gray-200 shadow-lg flex flex-col z-50 ${isDetachable ? 'cursor-move' : ''}`}
            style={isDetachable ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'grab'
            } : {}}
            onMouseDown={isDetachable ? handleMouseDown : undefined}
        >
            {/* Chat Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span className="font-semibold text-gray-900 text-sm">Group Chat</span>
                    {messages.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full">{messages.length}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{messages.length} messages</span>
                    {!isDetachable && (
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                            title="Collapse chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    )}
                    {isDetachable && onClose && (
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                            title="Close chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 flex flex-col justify-end">
                {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-6">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 text-gray-300">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <p className="text-xs font-medium">No messages yet</p>
                        <p className="text-xs">Start the conversation!</p>
                    </div>
                ) : (
                    <div className="flex flex-col space-y-3">
                        {messages.map((message) => (
                            <div key={message.id} className={`flex ${message.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xs ${message.senderId === currentUserId ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'} rounded-lg p-2 relative group`}>
                                    {/* Message Header */}
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium opacity-75">
                                            {message.senderName}
                                        </span>
                                        <span className="text-xs opacity-75">
                                            {formatTimestamp(message.timestamp)}
                                            {message.isEdited && <span className="ml-1">(edited)</span>}
                                        </span>
                                    </div>

                                    {/* Message Content */}
                                    {editingMessageId === message.id ? (
                                        <div className="space-y-2">
                                            <textarea
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                className="w-full p-2 text-xs border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                                                rows={2}
                                                autoFocus
                                                ref={(textarea) => {
                                                    if (textarea) {
                                                        textarea.focus();
                                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                                    }
                                                }}
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={saveEdit}
                                                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            {message.text && (
                                                <p className="text-xs whitespace-pre-wrap">{message.text}</p>
                                            )}
                                            {message.imageUrl && (
                                                <div className="mt-2">
                                                    <img
                                                        src={message.imageUrl}
                                                        alt="Chat image"
                                                        className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                                                        onClick={() => window.open(message.imageUrl, '_blank')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Message Actions (only for sender) */}
                                    {canEditMessage(message) && editingMessageId !== message.id && (
                                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => startEditing(message)}
                                                    className="p-1 text-xs bg-white bg-opacity-20 rounded hover:bg-opacity-30"
                                                    title="Edit message"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                                {canDeleteMessage(message) && (
                                                    <button
                                                        onClick={() => deleteMessage(message.id, message.imageUrl)}
                                                        className="p-1 text-xs bg-white bg-opacity-20 rounded hover:bg-opacity-30"
                                                        title="Delete message"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="3,6 5,6 21,6"></polyline>
                                                            <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Image Preview */}
            {imagePreview && (
                <div className="p-2 border-t border-gray-200 bg-gray-50">
                    <div className="relative inline-block">
                        <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-16 rounded border"
                        />
                        <button
                            onClick={removeImagePreview}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Message Input */}
            <div className="p-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex gap-2">
                    <div className="flex-1 relative">
                        <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message..."
                            className="w-full p-2 pr-8 border border-gray-300 rounded text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={1}
                            disabled={isLoading}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute right-1 bottom-1 p-1 text-gray-500 hover:text-blue-500 transition-colors"
                            disabled={isLoading}
                            title="Attach image"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21,15 16,10 5,21"></polyline>
                            </svg>
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                        />
                    </div>
                    <button
                        onClick={sendMessage}
                        disabled={(!newMessage.trim() && !selectedFile) || isLoading}
                        className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-xs"
                    >
                        {isLoading ? (
                            <>
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                {uploadingImage ? 'Uploading...' : 'Sending...'}
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                    <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                                </svg>
                                Send
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
} 